import React, { Component, createRef } from 'react';
import debounce from 'lodash.debounce';

// Helpers
import formatData from '../../helpers/formatData';
import generateLocations from '../../helpers/generateLocations';

// Variables
import variables from '../../theme/variables';

// Site settings
import siteSettings from '../../siteSettings.json';

// Styles
import {
	MapSearchContainerStyled,
	ListContainerStyled,
	MapContainerStyled,
	MobileContainerStyled,
} from './MapSearch.styled';

// Components
import LoadingContainer from '../../components/LoadingContainer/LoadingContainer';
import InteractiveMap from '../../components/InteractiveMap/InteractiveMap';
import RemoteContainer from '../../components/RemoteContainer/RemoteContainer';
import OverlayContainer from '../../components/OverlayContainer/OverlayContainer';

import List from '../../components/List/List';
import Filter from '../../components/Filter/Filter';
import SidePanel from '../../components/SidePanel/SidePanel';

class MapSearch extends Component {
	constructor(props) {
		super(props);
		// Reference to map so we can trigger events
		this.mapControllerRef = createRef();

		// Basic settings
		this.settings = {
			...siteSettings,
		};

		this.state = {
			allowInteraction: false,
			dataLoaded: false,
			data: { filtered: [], raw: [], locations: [], dropdownFilters: [] },
			currentSelected: { type: '', entry: {}, containsGeolocation: false },
			filters: { search: '', level: '' },
			sidePanelIsOpen: false,
			isMobileLayout: window.innerWidth <= variables.variables.breakPoint,
		};
	}

	componentDidMount() {
		fetch(this.settings.dataSource, {
			mode: 'cors',
		})
			.then((response) => response.json())
			.then((jsonData) => {
				// Format the JSON
				const formattedData = formatData(jsonData);

				// Determine the level filters
				let levelFilters = [];

				formattedData.forEach((entry) => {
					const option = { key: entry.level.toLowerCase(), name: entry.level };
					levelFilters[entry.level.toLowerCase()] = option;
				});

				const outputFilters = [];

				// Translate to regular array and add default
				const option = { key: '', name: 'none' };
				outputFilters.push(option);

				// Add the sorted options
				Object.entries(levelFilters).forEach((filterEntry) => {
					outputFilters.push(filterEntry[1]);
				});

				// Sort
				outputFilters.sort((a, b) => (b.key < a.key ? 1 : -1));

				// Update state
				this.setState({
					dataLoaded: true,
					data: {
						filtered: [...formattedData],
						raw: [...formattedData],
						locations: generateLocations([...formattedData]),
						dropdownFilters: outputFilters,
					},
				});
			})
			.catch((error) => console.error(error));

		// Add resize listeners
		window.addEventListener('resize', this.updateMode);
	}

	updateMode = () => {
		const isMobileLayout = window.innerWidth <= variables.variables.breakPoint;
		if (isMobileLayout !== this.state.isMobileLayout) {
			this.setState({ isMobileLayout: isMobileLayout });
		}
	};

	componentWillUnmount() {
		// Remove resize listeners
		window.removeEventListener('resize', this.update, true);
	}

	setSidePanel = (visible) => {
		this.setState({ sidePanelIsOpen: visible });
	};

	// Also set currentId for highlighting
	setHighlight = ({ entry }) => {
		this.setState({
			sidePanelIsOpen: true,
			currentSelected: {
				type: 'single',
				entry: entry,
				containsGeolocation:
					entry.hasOwnProperty('lat') && entry.hasOwnProperty('long'),
			},
		});

		// Move the map
		if (
			this.mapControllerRef.current !== null &&
			entry.hasOwnProperty('lat') &&
			entry.hasOwnProperty('long')
		) {
			this.mapControllerRef.current.setTarget(entry);
		}
	};

	setLocationHighlights = ({ entry }) => {
		this.setState({
			sidePanelIsOpen: true,
			currentSelected: {
				type: 'location',
				entry: entry,
				containsGeolocation: false,
			},
		});

		// Move the map
		this.mapControllerRef.current.setTarget(entry);
	};

	setCourseFilterDebounce = debounce((search, filter) => {
		// Make a copy
		const rawCopy = [...this.state.data.raw];

		// Filtered out the entries we do not need
		let filtered = rawCopy.filter((course) => {
			let searchMatch = false;
			let filterMatch = false;

			// Search
			if (search === '') {
				searchMatch = true;
			} else {
				this.settings.filterKeys.forEach((key) => {
					if (
						course[key] !== undefined &&
						course[key].toLowerCase().includes(search.toLowerCase())
					) {
						searchMatch = true;
					}
				});
			}

			// Filter
			if (filter === '') {
				filterMatch = true;
			} else {
				if (course['level'].toLowerCase().includes(filter)) {
					filterMatch = true;
				}
			}

			return filterMatch && searchMatch;
		});

		// Update state and spread the previous state
		this.setState({
			// Reset
			currentSelected: { type: '', entry: {}, containsGeolocation: false },
			sidePanelIsOpen: false,
			// Update the data
			data: {
				...this.state.data,
				filtered: [...filtered],
				locations: generateLocations([...filtered]),
			},
			// Set the filters
			filters: { search: search, level: filter },
		});
	}, 250);

	allowInteraction = () => {
		if (!this.state.allowInteraction) {
			this.setState({ allowInteraction: true });
		}
	};

	render() {
		// Boolean to toggle certain components and determine the position of the filters
		const isMobileLayout = this.state.isMobileLayout;
		const allowInteraction = this.state.allowInteraction;

		return (
			<MapSearchContainerStyled>
				{!this.state.dataLoaded && <LoadingContainer />}
				{this.state.dataLoaded && (
					<>
						<ListContainerStyled>
							{/* Show on Desktop */}
							{!isMobileLayout && (
								<Filter
									allowInteraction={this.allowInteraction}
									currentSearch={this.state.filters.search}
									currentFilter={this.state.filters.level}
									setCourseFilter={this.setCourseFilterDebounce}
									dropdownFilters={this.state.data.dropdownFilters}
								/>
							)}
							<List
								setHighlight={this.setHighlight}
								entries={this.state.data.filtered}
								currentSelected={this.state.currentSelected}
							/>
						</ListContainerStyled>
						<MapContainerStyled>
							{/* Only show on mobile */}
							{isMobileLayout && (
								<Filter
									allowInteraction={this.allowInteraction}
									currentSearch={this.state.filters.search}
									currentFilter={this.state.filters.level}
									setCourseFilter={this.setCourseFilterDebounce}
									dropdownFilters={this.state.data.dropdownFilters}
								/>
							)}
							{/* Show on Desktop */}
							{!isMobileLayout && (
								<>
									<SidePanel
										isMobileLayout={isMobileLayout}
										setSidePanel={this.setSidePanel}
										sidePanelIsOpen={this.state.sidePanelIsOpen}
										currentSelected={this.state.currentSelected}
									/>
									<RemoteContainer
										sidePanelIsOpen={this.state.sidePanelIsOpen}
										currentSelected={this.state.currentSelected}
									/>
								</>
							)}
							<InteractiveMap
								sidePanelIsOpen={this.state.sidePanelIsOpen}
								isMobileLayout={isMobileLayout}
								mapKey={this.settings.mapKey}
								ref={this.mapControllerRef}
								locations={this.state.data.locations}
								setLocationHighlights={this.setLocationHighlights}
							/>
						</MapContainerStyled>
						{/* Only show on mobile */}
						{isMobileLayout && this.state.sidePanelIsOpen && (
							<MobileContainerStyled>
								<SidePanel
									isMobileLayout={isMobileLayout}
									setSidePanel={this.setSidePanel}
									sidePanelIsOpen={this.state.sidePanelIsOpen}
									currentSelected={this.state.currentSelected}
								/>
							</MobileContainerStyled>
						)}
						{!allowInteraction && (
							<OverlayContainer allowInteraction={this.allowInteraction} />
						)}
					</>
				)}
			</MapSearchContainerStyled>
		);
	}
}

export default MapSearch;
